import { useMemo, useState, type ReactNode } from "react";
import { Navigate, useSearchParams } from "react-router";
import { BookCheck, Building2, Fingerprint, KeyRound, Languages, Loader2, LogIn, ShieldCheck, Sparkles, UserRound, type LucideIcon } from "lucide-react";
import { isApiError, usePersonas, useSession, useSignInGate, type Persona, type TenantKind } from "@pmo/api-client";
import { enumLabel, pickLocalized, useLocale, useTranslation } from "@pmo/i18n";
import { Avatar, Badge, Button, GovBar, GovFooter, InlineAlert, Skeleton, TenantBadge, cn, getSafeRedirect } from "@pmo/ui";
import { ApiUnavailable, FullPageLoader } from "./states";

export interface LoginPageProps {
  /** The app's name under the logo (none by default: the logo names the platform). */
  appName?: string;
  /** Tenant kinds whose personas are listed first (e.g. `["platform"]` in the console). */
  preferKinds?: TenantKind[];
  /** Where to go after sign-in when no `returnUrl` is given. */
  defaultPath?: string;
  footer?: ReactNode;
}

interface PersonaGroup {
  code: string;
  name: string;
  kind: TenantKind;
  personas: Persona[];
}

const FEATURES: Array<{ key: "ai" | "guidebook" | "bilingual"; icon: LucideIcon }> = [
  { key: "ai", icon: Sparkles },
  { key: "guidebook", icon: BookCheck },
  { key: "bilingual", icon: Languages },
];

const SSO_ERRORS: Record<string, string> = {
  not_enrolled: "auth.notEnrolled",
  not_member: "auth.notMember",
  uaepass_level: "auth.uaePassLevel",
};

/**
 * Sign-in, on the UAE Design System 3.0 page (header with the UAE emblem, a bordered sign-in card, the footer): UAE PASS, UAE
 * Government SSO (OIDC) and, in development, one card per seeded persona grouped by tenant. Which methods
 * appear follows the session's `auth` flags. `?ws=CODE` makes this a ministry's own sign-in gate: it names the ministry, offers the
 * ministry's directory when it has one, and only opens that ministry's workspace. Returns to `returnUrl` afterwards (same-origin paths only).
 */
export function LoginPage({ appName, preferKinds, defaultPath = "/", footer }: LoginPageProps) {
  const { t } = useTranslation("common");
  const { locale, setLocale } = useLocale();
  const [params, setParams] = useSearchParams();
  const { session, isLoading, error, refetch, isAuthenticated, devLogin, ssoUrl } = useSession();
  const flags = session?.auth ?? { devLogin: false, oidc: false };
  const personas = usePersonas(!!session && flags.devLogin && !isAuthenticated);
  const gateCode = params.get("ws");
  const gateQuery = useSignInGate(gateCode);
  const gate = gateQuery.data ?? null;
  const [pending, setPending] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const returnUrl = getSafeRedirect(params.get("returnUrl"), defaultPath);
  const signedOut = params.has("signedOut");
  const ssoError = params.get("error");
  const gateName = gate ? pickLocalized(locale, gate.nameEn, gate.nameAr) : "";

  const groups = useMemo<PersonaGroup[]>(() => {
    const map = new Map<string, PersonaGroup>();
    for (const p of personas.data ?? []) {
      if (gate && p.tenantCode !== gate.code) continue;
      const g = map.get(p.tenantCode) ?? { code: p.tenantCode, name: pickLocalized(locale, p.tenantNameEn, p.tenantNameAr), kind: p.tenantKind, personas: [] };
      g.personas.push(p);
      map.set(p.tenantCode, g);
    }
    const rank = (k: TenantKind) => (preferKinds?.includes(k) ? 0 : k === "ministry" ? 1 : k === "oversight" ? 2 : 3);
    return [...map.values()].sort((a, b) => rank(a.kind) - rank(b.kind) || a.name.localeCompare(b.name, locale));
  }, [personas.data, locale, preferKinds, gate]);

  if (isLoading) return <FullPageLoader />;
  if (error && !session) {
    return (
      <ApiUnavailable
        retrying={retrying}
        onRetry={() => {
          setRetrying(true);
          void refetch().finally(() => setRetrying(false));
        }}
      />
    );
  }
  if (isAuthenticated) return <Navigate to={returnUrl} replace />;

  const signInAs = async (p: Persona) => {
    setPending(p.username);
    setFailure(null);
    try {
      await devLogin(p.username);
    } catch (e) {
      setFailure(isApiError(e) && e.isUnavailable ? t("errors.apiUnavailableTitle") : isApiError(e) ? e.detail ?? e.title : t("errors.genericTitle"));
    } finally {
      setPending(null);
    }
  };

  const noMethods = !flags.devLogin && !flags.oidc;
  const ws = gate?.code;
  const go = (idp?: string) => window.location.assign(ssoUrl(returnUrl, locale, { idp, ws }));
  const leaveGate = () => {
    const next = new URLSearchParams(params);
    next.delete("ws");
    next.delete("error");
    setParams(next, { replace: true });
  };

  return (
    <div className="flex min-h-dvh flex-col bg-white">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:start-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:shadow">
        {t("app.skipToContent")}
      </a>
      <GovBar
        ministryName={t("app.owner")}
        ministryNameSecondary={t("app.government")}
        platformName={appName}
        locale={locale}
        onToggleLocale={() => setLocale(locale === "ar" ? "en" : "ar")}
        emblem
      />
      <main id="main" tabIndex={-1} className="mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-4 py-10 outline-none sm:px-6 lg:px-8 lg:py-16">
        <div className={cn("grid items-start gap-10 lg:gap-14", flags.devLogin ? "lg:grid-cols-[minmax(0,28rem)_minmax(0,1fr)]" : "lg:grid-cols-[minmax(0,1fr)_minmax(0,30rem)]")}>
          <section aria-labelledby="signin-title" className={cn("flex flex-col gap-8", !flags.devLogin && "lg:order-2")}>
            <div className="flex flex-col gap-5 rounded-xl border border-aegold-200 bg-white p-6 shadow-[0_10px_15px_-3px_rgb(27_29_33/0.08),0_4px_6px_-4px_rgb(27_29_33/0.08)] sm:p-8">
              <div className="flex flex-col gap-2">
                {gate ? <TenantBadge name={gateName} kind={gate.kind} code={gate.code} accent={gate.accent ?? undefined} size="sm" className="w-fit" /> : null}
                <h1 id="signin-title" className="font-heading text-[1.625rem] leading-tight font-bold text-aeblack-900">
                  {gate ? t("auth.gateTitle", { name: gateName }) : t("auth.title")}
                </h1>
                <p className="text-aeblack-600">{gate ? t("auth.gateSubtitle") : t("auth.subtitle")}</p>
              </div>
              {gateCode && gateQuery.data === null ? <InlineAlert tone="warning">{t("auth.gateUnknown")}</InlineAlert> : null}
              {signedOut && !failure && !ssoError ? <InlineAlert tone="success">{t("auth.signedOut")}</InlineAlert> : null}
              {ssoError ? <InlineAlert tone="danger">{t(SSO_ERRORS[ssoError] ?? "auth.ssoFailed", { name: gateName || t("auth.thisWorkspace") })}</InlineAlert> : null}
              {flags.oidc && flags.uaePass ? (
                <div className="flex flex-col gap-2">
                  <Button size="lg" primary className="w-full bg-aeblack-800 hover:bg-aeblack-950" onClick={() => go("uaepass")}>
                    <Fingerprint aria-hidden />
                    {t("auth.uaePass")}
                  </Button>
                  <p className="text-sm text-aeblack-600">{t("auth.uaePassHint")}</p>
                </div>
              ) : null}
              {flags.oidc && gate?.directory ? (
                <Button size="lg" variant="secondary" className="w-full" onClick={() => go(gate.directory?.alias)}>
                  <Building2 aria-hidden />
                  {t("auth.directory", { name: pickLocalized(locale, gate.directory.nameEn, gate.directory.nameAr) })}
                </Button>
              ) : null}
              {flags.oidc ? (
                <div className="flex flex-col gap-2">
                  <Button size="lg" variant={flags.uaePass ? "outline" : undefined} primary={!flags.uaePass} className="w-full" onClick={() => go()}>
                    <ShieldCheck aria-hidden />
                    {t("auth.sso")}
                  </Button>
                  <p className="text-sm text-aeblack-600">{t("auth.ssoHint")}</p>
                </div>
              ) : null}
              {gate ? (
                <button type="button" onClick={leaveGate} className="link-ds w-fit text-sm font-medium">
                  {t("auth.leaveGate", { name: gateName })}
                </button>
              ) : null}
              {noMethods ? <InlineAlert tone="warning">{t("auth.noMethods")}</InlineAlert> : null}
              {failure ? <InlineAlert tone="danger">{failure}</InlineAlert> : null}
            </div>
            {flags.devLogin ? <Intro footer={footer} /> : null}
          </section>

          {flags.devLogin ? (
            <section aria-labelledby="personas-title" className="flex flex-col gap-4 rounded-xl bg-aeblack-50 p-5 sm:p-6">
              <div className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <KeyRound aria-hidden className="size-4 text-camel-700" />
                  <h2 id="personas-title" className="font-heading text-base font-bold text-aeblack-900">{t("auth.devTitle")}</h2>
                  <Badge variant="warning" size="sm">{t("auth.devBadge")}</Badge>
                </div>
                <p className="text-sm text-aeblack-600">{t("auth.devHint")}</p>
              </div>
              {personas.isLoading ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
                </div>
              ) : personas.error ? (
                isApiError(personas.error) && personas.error.isUnavailable ? (
                  <ApiUnavailable fullPage={false} onRetry={() => void personas.refetch()} retrying={personas.isFetching} />
                ) : (
                  <InlineAlert tone="warning">{t("auth.personasUnavailable")}</InlineAlert>
                )
              ) : (
                <div className="flex flex-col gap-5">
                  {groups.map((g) => (
                    <div key={g.code} className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <TenantBadge name={g.name} kind={g.kind} code={g.code} size="sm" />
                        <span className="text-xs text-aeblack-600">{enumLabel(locale, "tenantKind", g.kind)}</span>
                      </div>
                      <ul className="grid gap-2 sm:grid-cols-2">
                        {g.personas.map((p) => {
                          const name = pickLocalized(locale, p.displayNameEn, p.displayNameAr);
                          const busy = pending === p.username;
                          return (
                            <li key={p.username}>
                              <button
                                type="button"
                                disabled={!!pending}
                                onClick={() => void signInAs(p)}
                                aria-label={t("auth.signInAs", { name })}
                                className={cn(
                                  "group flex w-full items-center gap-3 rounded-lg border border-aeblack-100 bg-white p-3 text-start transition-colors duration-300 hover:border-aegold-300 hover:bg-aegold-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-60",
                                  busy && "border-aegold-600 bg-aegold-50",
                                )}
                              >
                                <Avatar name={name} />
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate text-sm font-semibold text-aeblack-900">{name}</span>
                                  <span className="block truncate text-xs text-aeblack-600">{pickLocalized(locale, p.titleEn, p.titleAr)}</span>
                                  <span className="mt-1 flex flex-wrap gap-1">
                                    {p.roles.slice(0, 2).map((r) => (
                                      <Badge key={r} variant="outline" size="sm">{enumLabel(locale, "role", r)}</Badge>
                                    ))}
                                  </span>
                                </span>
                                {busy ? <Loader2 className="size-4 animate-spin text-aegold-700" aria-hidden /> : <LogIn className="size-4 text-aeblack-300 transition-colors group-hover:text-aegold-700 rtl:-scale-x-100" aria-hidden />}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                  {!groups.length ? (
                    <p className="flex items-center gap-2 text-sm text-aeblack-600">
                      <UserRound className="size-4" aria-hidden />
                      {t("states.empty")}
                    </p>
                  ) : null}
                </div>
              )}
            </section>
          ) : (
            <div className="lg:order-1 lg:pt-4">
              <Intro footer={footer} />
            </div>
          )}
        </div>
      </main>
      <GovFooter>{t("app.copyright", { year: new Date().getFullYear() })}</GovFooter>
    </div>
  );
}

/** What the platform is, beside the sign-in card: the eyebrow, the headline and three of its features. */
function Intro({ footer }: { footer?: ReactNode }) {
  const { t } = useTranslation("common");
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <p className="inline-flex items-center gap-2 text-sm font-semibold text-aegold-700">
          <ShieldCheck aria-hidden className="size-4" />
          {t("auth.heroEyebrow")}
        </p>
        <p className="max-w-xl font-heading text-[2.25rem] leading-[1.15] font-extrabold text-balance text-aeblack-900 sm:text-[2.75rem]">{t("auth.heroTitle")}</p>
      </div>
      <ul className="flex flex-col gap-4">
        {FEATURES.map(({ key, icon: Icon }) => (
          <li key={key} className="flex items-center gap-4 text-aeblack-700">
            <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-aegold-50 text-aegold-600">
              <Icon aria-hidden className="size-5" />
            </span>
            <span>{t(`auth.features.${key}`)}</span>
          </li>
        ))}
      </ul>
      {footer}
    </div>
  );
}
